import { useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface Tab {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  badge?: ReactNode;
  content: ReactNode;
}

interface TabsProps {
  tabs: Tab[];
  defaultId?: string;
  onChange?: (id: string) => void;
  className?: string;
  variant?: 'pills' | 'underline';
}

export function Tabs({
  tabs,
  defaultId,
  onChange,
  className,
  variant = 'underline',
}: TabsProps) {
  const [active, setActive] = useState(defaultId ?? tabs[0]?.id);

  function setTab(id: string) {
    setActive(id);
    onChange?.(id);
  }

  const current = tabs.find((t) => t.id === active);

  return (
    <div className={className}>
      <div
        role="tablist"
        className={cn(
          variant === 'pills'
            ? 'inline-flex gap-1 p-1 bg-slate-100 rounded-lg'
            : 'flex gap-1 border-b border-slate-200'
        )}
      >
        {tabs.map((t) => {
          const isActive = t.id === active;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setTab(t.id)}
              className={cn(
                'inline-flex items-center gap-2 text-sm font-medium transition-colors px-3 py-2',
                variant === 'pills'
                  ? cn(
                      'rounded-md',
                      isActive
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    )
                  : cn(
                      '-mb-px border-b-2',
                      isActive
                        ? 'border-brand-600 text-brand-700'
                        : 'border-transparent text-slate-500 hover:text-slate-900 hover:border-slate-300'
                    )
              )}
            >
              {t.icon}
              <span>{t.label}</span>
              {t.badge && <span className="ml-1">{t.badge}</span>}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" className="mt-5">
        {current?.content}
      </div>
    </div>
  );
}
